import { Feature, FeatureCollection, LineString as GeoJSONLineString, Point } from 'geojson';
import { create, findNear, removeById, updateById } from '../../api/operation/roadNet';
import SingletonEventEmitterClass from '../../classes/SingletonEventEmitterClass';

import { LS_ROAD_NET_STORES_KEY } from '../../utils/constants';
import LineString from './LineString';

interface ILineStringStore {
  lineString: LineString;
  remote?: string;
}

interface ILineStringStoreInLocalStorage {
  points: ILngLat[];
  remote?: string;
}

interface IRemoteLineString {
  _id: string;
  hash: string;
  gcj02Geometry: {
    type: 'LineString';
    coordinates: number[][];
    properties?: IObject;
  };
  geometry: {
    type: 'LineString';
    coordinates: number[][];
    properties?: IObject;
  };
}

export default class RoadNetLoader extends SingletonEventEmitterClass {
  private _data: IRemoteLineString[] = [];
  private _editor?: LineString;
  private _lineStringStores?: ILineStringStore[];
  private _center?: ILngLat;
  private _editorRemote?: string;
  public _roadNetDefaultRoadType?: number;

  public get geojson(): FeatureCollection<GeoJSONLineString> {
    return {
      type: 'FeatureCollection',
      features: this._data.map<Feature<GeoJSONLineString>>(item => {
        return {
          type: 'Feature',
          geometry: item.geometry,
          properties: {
            id: item._id,
            hash: item.hash,
            info: { ...item, layerType: 'roadNet' },
          },
        };
      }),
    };
  }

  public get geojsonPoints(): FeatureCollection<Point> {
    const data = this._data.reduce<Array<Feature<Point>>>((prev, next) => {
      return [...prev, ...next.geometry.coordinates.map(item => {
        return {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: item,
          },
          properties: {
            id: next._id,
          },
        };
      })] as Array<Feature<Point>>;
    }, [] as Array<Feature<Point>>);

    const stores = this._lineStringStores!.reduce<Array<Feature<Point>>>((prev, next) => {
      if (next.lineString.id === this.editorRemote) {
        return prev;
      } else {
        return [...prev, ...next.lineString.geometry.coordinates.map(item => {
          return {
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: item,
            },
            properties: {
              id: next.remote,
            },
          };
        })] as Array<Feature<Point>>;
      }
    }, [] as Array<Feature<Point>>);
    return {
      type: 'FeatureCollection',
      features: [...data, ...stores],
    };
  }

  constructor() {
    super();
    if (RoadNetLoader.__new) {
      this._lineStringStores = this._readFromLocalStorage();
      document.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.code === 'Backspace') {
          if (this._editor) {
            const lineStringStores = this._lineStringStores!;
            const lineStringStore = lineStringStores.find(store => store.lineString === this._editor);
            if (lineStringStore) {
              lineStringStores.splice(lineStringStores.indexOf(lineStringStore), 1);
              if (lineStringStore.remote) {
                removeById(lineStringStore.remote).catch(error => {
                  console.error(error);
                  this.refresh();
                });
              }
            }
            this._editor = undefined;
            this._editorRemote = undefined;
            this._triggerLineStringUpdated();
            this._triggerEditorUpdated();
            this._triggerLocalUpdated();
            this._triggerPointsUpdated();
          }
        }
      });
    }
  }

  private get _updatedRemoteLineStringIds(): string[] {
    return this._lineStringStores!.filter(store => store.remote).map(store => store.remote!);
  }

  public refresh() {
    if (this._center) {
      this._fetchData(this._center);
    }
  }

  public get geojsonEditor(): FeatureCollection<Point> {
    if (!this._editor) {
      return {
        type: 'FeatureCollection',
        features: [],
      };
    }
    return this._editor.geojsonEditor;
  }

  public get editor() {
    return this._editor;
  }

  public get editorRemote() {
    return this._editorRemote;
  }

  public get geojsonEditLocalRoadNet(): FeatureCollection<GeoJSONLineString> {
    return {
      type: 'FeatureCollection',
      features: this._lineStringStores!.map<Feature<GeoJSONLineString>>(store => {
        return store.lineString.geojsonLineString;
      }),
    };
  }

  public selectLocalLineString(id: string) {
    const store = this._lineStringStores!.find(lineStringStore => {
      return lineStringStore.lineString.id === id;
    });
    if (!store) {
      return;
    }
    this._editorRemote = this._editor === store.lineString ? undefined : store.lineString.id;
    this._editor = this._editor === store.lineString ? undefined : store.lineString;
    this._triggerEditorUpdated();
    this._triggerPointsUpdated();
  }

  public onMapClickedRemoteLineString(id: string) {
    const remoteLineStrings = this._data!;
    const remoteLineString = remoteLineStrings.find(lineString => lineString._id === id) as IRemoteLineString;
    if (remoteLineString) {
      const points = remoteLineString.geometry.coordinates;
      this.addLineString(points as ILngLat[], id);
      remoteLineStrings.splice(remoteLineStrings.indexOf(remoteLineString), 1);
      this._triggerEditorUpdated();
      this._triggerLineStringUpdated();
      this._triggerLocalUpdated();
      this._triggerPointsUpdated();
    }
  }

  public get lineStringStoresCount() {
    return this._lineStringStores!.length;
  }

  public async publish() {
    if (this.lineStringStoresCount === 0) {
      return;
    }
    this._editor = undefined;
    this._editorRemote = undefined;
    const stores = this._lineStringStores!;
    while (stores.length > 0) {
      const store = stores[0];
      if (store.remote) {
        await updateById(store.remote, store.lineString.geometry.coordinates as ILngLat[]);
      } else {
        // await create(store.lineString.geometry.coordinates as ILngLat[], this.roadNetDefaultRoadType);
        await create(store.lineString.geometry.coordinates as ILngLat[], this._roadNetDefaultRoadType);
      }
      stores.splice(0, 1);
      this._triggerLineStringUpdated();
      this._triggerLocalUpdated();
      this._triggerEditorUpdated();
      this._triggerPointsUpdated();
    }
    this.refresh();
  }

  public async reset() {
    this._lineStringStores = [];
    this._editor = undefined;
    this._editorRemote = undefined;
    this._triggerLocalUpdated();
    this._triggerEditorUpdated();
    this._triggerLineStringUpdated();
    this._triggerPointsUpdated();
    this.refresh();
  }

  public cancelEditing() {
    this._editor = undefined;
    this._editorRemote = undefined;
    this._triggerEditorUpdated();
    this._triggerPointsUpdated();
  }

  public onMapUpdatedCenter(center: ILngLat) {
    this._center = center;
    this.refresh();
  }

  public addLineString(points: ILngLat[], remote?: string) {
    const lineString = this._newLineString(points);
    const sameLineStringStore = this._lineStringStores!.find(store => {
      return store.lineString.hash === lineString.hash;
    });
    if (sameLineStringStore) {
      return;
    }
    this._lineStringStores!.push({
      lineString,
      remote,
    });
    this._editor = lineString;
    this._editorRemote = lineString.id;
    this._triggerLocalUpdated();
    this._triggerEditorUpdated();
  }

  private async _fetchData(center: ILngLat) {
    const { data } = await findNear(center, 100000, 'wgs84');
    const lineStrings = data.map(item => {
      return { ...item.line, _id: item._id, creator: item.creator, length: item.length, roadType: item.roadType };
    });
    const updatedRemoteLineStringIds = this._updatedRemoteLineStringIds;
    this._data = lineStrings.filter(item => !updatedRemoteLineStringIds.includes(item._id));
    this._triggerLineStringUpdated();
    this._triggerPointsUpdated();
  }

  private _triggerLineStringUpdated() {
    this.emit('updated');
    this._saveToLocalStorage();

  }

  private _triggerLocalUpdated() {
    this.emit('local_updated');
  }

  private _triggerEditorUpdated() {
    this.emit('editor_updated');
    this._saveToLocalStorage();
  }

  private _triggerPointsUpdated() {
    this.emit('points_updated');
    this._saveToLocalStorage();
  }

  private _saveToLocalStorage() {
    localStorage.setItem(LS_ROAD_NET_STORES_KEY, JSON.stringify(this._lineStringStores!.map((lineStringStore: ILineStringStore) => {
      return {
        points: lineStringStore.lineString.points,
        remote: lineStringStore.remote,
      } as ILineStringStoreInLocalStorage;
    })));
  }

  private _readFromLocalStorage() {
    const data = localStorage.getItem(LS_ROAD_NET_STORES_KEY);
    if (data) {
      try {
        const lineStringStores = JSON.parse(data) as ILineStringStoreInLocalStorage[];
        return lineStringStores.map(lineStringStore => {
          return {
            lineString: this._newLineString(lineStringStore.points),
            remote: lineStringStore.remote,
          } as ILineStringStore;
        });
      } catch (e) {
        return [];
      }
    } else {
      return [];
    }
  }

  private _newLineString(points: ILngLat[]) {
    const lineString = new LineString(points);
    lineString.on('updated', () => {
      this._triggerEditorUpdated();
      this._triggerLocalUpdated();
      this._triggerPointsUpdated();
    });
    return lineString;
  }

}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--1-0!./src/classes/loaders/RoadNetLoader.ts